/* Görünüm modeli — veritabanı satırından şablonun beklediği alanlara.

   Sayfadaki her metin ve her sayı biçimi burada kararlaştırılır; şablonlar
   yalnızca yerleştirir. Elle yazılmış siteyle birebir aynı çıktı vermek
   milestone 4'ün kapısı olduğu için biçimler (üç haneli `--r`, "— büyüt"
   eki, Türkçe tarih) burada tek tek sabitlenmiştir. */

import { pick } from '@frawgraphy/shared';
import type { PageSeo } from '@frawgraphy/shared';
import type { AlbumRow, PhotoRow, PostRow, ServiceRow, SiteContent, WorkRow } from './content.ts';
import { mediaUrls, type Base, type UrlMode } from './urls.ts';

export type Lang = 'tr' | 'en';

export const SITE_ORIGIN = 'https://frawgraphy.com';

/* `--r` üç haneli yazılır (0.800, 0.801), `--s` gereksiz sıfır taşımaz
   (1, 1.15, 1.3). Postgres `real` sütunu 0.667'yi 0.6669999… olarak geri
   verdiği için ikisi de yuvarlanmadan yazılamaz. */
export const cssRatio = (value: number) => value.toFixed(3);
export const cssWeight = (value: number) => String(Number(value.toFixed(2)));

/** Albüm ızgarasındaki boy ritmi — kareler sırayla bu ağırlıkları alır. */
export const ALBUM_WEIGHTS = [1.3, 1, 1.15] as const;

const TR_MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
] as const;

/** "2026-06-18" → "18 Haziran 2026". */
export function trDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const month = TR_MONTHS[(m ?? 1) - 1];
  if (!y || !d || !month) throw new Error(`tarih okunamadı: ${iso}`);
  return `${d} ${month} ${y}`;
}

/** Sayfa iskeletinin bağlam değişkenleri — bağlantıların derinliği burada. */
export type Chrome = {
  lang: Lang;
  base: Base;
  home: string;
  brandHref: string;
  brandAria: string;
  solid: boolean;
  interactive: boolean;
};

/** Kök dizindeki bir iç sayfa (works, blog, album). */
export const innerChrome = (lang: Lang): Chrome => ({
  lang,
  base: '',
  home: 'index.html',
  brandHref: 'index.html',
  brandAria: 'Frawgraphy ana sayfa',
  solid: true,
  interactive: false
});

/** Ana sayfa: başlık şeffaf, sekme göstergesi ve bölüm takibi açık. */
export const homeChrome = (lang: Lang): Chrome => ({
  ...innerChrome(lang),
  home: '',
  brandHref: '#hero',
  brandAria: 'Frawgraphy',
  solid: false,
  interactive: true
});

/**
 * 404: derinliği bilinmeyen sayfa, o yüzden her bağlantı kök göreli.
 *
 * Cloudflare Pages aynı dosyayı `/yok` ve `/blog/yok` için de servis eder.
 * Göreli bir önek ikincisinde bir dizin şaşar; `'/'` ikisinde de doğru.
 */
export const notFoundChrome = (lang: Lang): Chrome => ({
  ...innerChrome(lang),
  base: '/',
  home: '/',
  brandHref: '/'
});

/** blog/ altındaki yazı sayfası — bütün bağlantılar bir dizin yukarı. */
export const postChrome = (lang: Lang): Chrome => ({
  ...innerChrome(lang),
  base: '../',
  home: '../index.html',
  brandHref: '../index.html'
});

type Ctx = { lang: Lang; mode: UrlMode };

const frame = (photo: PhotoRow, lang: Lang, base: Base, ctx: Ctx) => {
  const media = mediaUrls(ctx.mode, base);
  const alt = pick(photo.alt, lang);
  return {
    id: photo.id,
    browse: media.browse(photo),
    full: media.full(photo),
    alt,
    width: photo.width,
    height: photo.height,
    zoomAria: `${alt} — büyüt`
  };
};

export function coverflowView(content: SiteContent, ctx: Ctx) {
  return content.featured.map((p) => frame(p, ctx.lang, '', ctx));
}

export function servicesView(services: ServiceRow[], lang: Lang) {
  return services.map((s) => {
    const title = pick(s.title, lang);
    return {
      title,
      desc: pick(s.desc, lang),
      meta: pick(s.meta, lang),
      /* "Editorial" ve "Lookbook" Türkçe metinde de İngilizce sözcüklerdir;
         elle yazılmış sayfa bunları `data-tr-lang="en"` ile işaretliyor ki
         ekran okuyucu doğru telaffuz etsin. Kural: iki dilde aynı yazılan
         başlık İngilizce sayılır. */
      englishTitle: s.title.en !== undefined && s.title.en === s.title.tr
    };
  });
}

export function worksView(content: SiteContent, ctx: Ctx) {
  const albumBySlug = new Map(content.albums.map((a) => [a.slug, a]));
  return content.works.map((w: WorkRow) => {
    const base: Base = '';
    const view = {
      ...frame(w.photo, ctx.lang, base, ctx),
      photo: { width: w.photo.width, height: w.photo.height },
      r: cssRatio(w.ratio),
      s: cssWeight(w.weight),
      category: pick(w.category, ctx.lang),
      year: w.year,
      base,
      albumSlug: w.albumSlug,
      album: null as null | { title: string; count: number; countLabel: string; aria: string }
    };
    if (w.albumSlug) {
      const album = albumBySlug.get(w.albumSlug);
      if (!album) throw new Error(`albüm bulunamadı: ${w.albumSlug}`);
      const count = album.photos.length;
      const title = pick(album.title, ctx.lang);
      view.album = {
        title,
        count,
        countLabel: `${count} kare`,
        aria: `${title} albümü — ${count} kare`
      };
    }
    return view;
  });
}

const absolute = (path: string | null) => (path ? `${SITE_ORIGIN}/${path.replace(/^\//, '')}` : '');

/** Gövde metnindeki sözcük sayısı — JSON-LD `wordCount` için. */
export function wordCount(...html: string[]): number {
  return html
    .join(' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&[a-z]+;|&#\d+;/gi, ' ')
    .split(/\s+/)
    .filter(Boolean).length;
}

const seoOf = (seo: PageSeo | null, lang: Lang, what: string) => {
  if (!seo) throw new Error(`SEO metni eksik: ${what}`);
  return {
    docTitle: pick(seo.docTitle, lang),
    description: pick(seo.description, lang),
    ogTitle: pick(seo.ogTitle, lang),
    ogDescription: pick(seo.ogDescription, lang),
    ogImage: absolute(seo.ogImage)
  };
};

export function albumView(album: AlbumRow, ctx: Ctx) {
  const base: Base = '';
  return {
    slug: album.slug,
    title: pick(album.title, ctx.lang),
    titleHtml: pick(album.titleHtml, ctx.lang),
    lede: pick(album.lede, ctx.lang),
    year: album.year,
    count: album.photos.length,
    canonical: `${SITE_ORIGIN}/album-${album.slug}.html`,
    seo: seoOf(album.seo, ctx.lang, `album ${album.slug}`),
    photos: album.photos.map((p, i) => ({
      ...frame(p, ctx.lang, base, ctx),
      r: cssRatio(p.ratio),
      s: cssWeight(ALBUM_WEIGHTS[i % ALBUM_WEIGHTS.length]!),
      year: album.year
    }))
  };
}

export function postCardsView(posts: PostRow[], lang: Lang) {
  return posts.map((p, i) => ({
    slug: p.slug,
    i18n: `blog.${i + 1}`,
    category: pick(p.category, lang),
    date: p.publishedAt,
    dateText: trDate(p.publishedAt),
    readMinutes: p.readMinutes,
    title: pick(p.title, lang),
    excerpt: pick(p.excerpt, lang),
    cover: p.cover
      ? {
          listUrl: p.cover.listUrl,
          listAlt: pick(p.cover.listAlt, lang),
          listW: p.cover.listW,
          listH: p.cover.listH
        }
      : null
  }));
}

/** Yazılar arası gezinme: en yeniden en eskiye, iki uçta "Tüm yazılar". */
export function postPageView(posts: PostRow[], index: number, lang: Lang) {
  const p = posts[index]!;
  const link = (other: PostRow | undefined) =>
    other
      ? { href: `${other.slug}.html`, label: pick(other.shortTitle, lang) }
      : { href: '../blog.html', label: 'Tüm yazılar' };

  return {
    post: {
      slug: p.slug,
      shortTitle: pick(p.shortTitle, lang),
      title: pick(p.title, lang),
      titleHtml: pick(p.titleHtml, lang),
      lede: pick(p.lede, lang),
      category: pick(p.category, lang),
      date: p.publishedAt,
      dateText: trDate(p.publishedAt),
      readMinutes: p.readMinutes,
      bodyHtml: pick(p.bodyHtml, lang),
      canonical: `${SITE_ORIGIN}/blog/${p.slug}.html`,
      seo: seoOf(p.seo, lang, `yazı ${p.slug}`),
      wordCount: wordCount(pick(p.lede, lang), pick(p.bodyHtml, lang)),
      /* JSON-LD gövdesine giren değerler HTML değil JSON olarak kaçırılır;
         şablon bunları ham yazar. `JSON.stringify` tırnakları da koyar. */
      jsonld: {
        headline: JSON.stringify(pick(p.seo?.ogTitle ?? p.title, lang)),
        description: JSON.stringify(pick(p.seo?.ogDescription ?? p.title, lang)),
        keywords: JSON.stringify(p.seo?.keywords ? pick(p.seo.keywords, lang) : ''),
        shortTitle: JSON.stringify(pick(p.shortTitle, lang))
      },
      cover: p.cover
        ? {
            url: p.cover.url,
            alt: pick(p.cover.alt, lang),
            caption: pick(p.cover.caption, lang),
            w: p.cover.w,
            h: p.cover.h
          }
        : null
    },
    prev: link(posts[index - 1]),
    next: link(posts[index + 1])
  };
}
